import { Component, Input, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { GolfEventService } from '../../../services/golf-event.service';
import { GolfEvent } from '../../../models/GolfEvent';
import { GolfEventField } from '../../../models/golfeventfield';
import { NgFor, NgIf } from '@angular/common';
import { UiSwitchModule } from 'ngx-ui-switch';
import { ApplicationService } from '../../../services/application.service';
import { Team } from '../../../models/team';
import { AuthenticationService } from '../../../services/authentication.service';
import { PERMISSIONS, EVENT_FORMAT } from '../../../models/constants';
import { AddToFieldComponent } from './add-to-field/add-to-field.component';
import { CreateTeamComponent } from '../create-team/create-team.component';
import { isEventOver } from '../../../util/dateutil';

interface TeamRowViewModel {
  team: Team;
  isConfirmingDelete: boolean;
}

@Component({
  selector: 'app-field',
  standalone: true,
  imports: [NgFor, NgIf, UiSwitchModule, AddToFieldComponent, CreateTeamComponent],
  templateUrl: './field.component.html',
  styleUrl: './field.component.css'
})
export class FieldComponent implements OnChanges, OnInit {
  constructor(private golfEventService:GolfEventService, private applicationService:ApplicationService, private authenticationService:AuthenticationService) {

  }

  async ngOnInit(): Promise<void> {
    this.isEventAdmin = await this.authenticationService.hasPermission(PERMISSIONS.EVENTADMIN);
    if (this.authenticationService.loginResult?.player) {
      this.loggedInPlayerId = this.authenticationService.loginResult.player.playerId;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['league'] && this.league) {
      // Hybrid scramble pairs up per round, so there are no fixed teams to show.
      this.isTeamEvent = this.league.isTeamEvent && this.league.type !== EVENT_FORMAT.HYBRID_SCRAMBLE;
      this.teams = [];
      this.teamRows = [];
      this.searchTeams();
      this.getField(this.league.id);
    }
  }

  @Input() league:GolfEvent|null = null;
  field:Array<GolfEventField> = [];
  containerDisplay = 'INDIVIDUAL'
  isTeamEvent = false;
  teams:Array<Team> = [];
  teamRows:Array<TeamRowViewModel> = [];
  teamToEdit:Team|null = null;
  playersForTeamForm:Array<GolfEventField> = [];
  isEventAdmin:boolean = false;
  loggedInPlayerId:number|null = null;

  get paidPlayers():Array<GolfEventField> {
    return this.field.filter(p => p.hasPaid);
  }

  get isEventOver():boolean {
    return isEventOver(this.league?.endDate);
  }

  get canAddToField():boolean {
    return this.isEventAdmin && !this.isEventOver;
  }

  buildPlayersForTeamForm(team:Team|null = null):Array<GolfEventField> {
    const byId = new Map<number, GolfEventField>();
    this.paidPlayers.forEach(p => byId.set(Number(p.playerId), p));

    if (team) {
      for (const teamPlayer of team.players) {
        const playerId = Number(teamPlayer.id);
        if (byId.has(playerId)) continue;
        const fromField = this.field.find(f => Number(f.playerId) === playerId);
        byId.set(playerId, fromField || {
          id: -1,
          golfEventId: this.league?.id ?? -1,
          playerId: playerId,
          hasPaid: false,
          firstName: teamPlayer.firstName,
          lastName: teamPlayer.lastName,
          isDeleted: false,
          handicapIndex: null
        });
      }
    }

    return Array.from(byId.values());
  }

  async getField(golfEventId:number):Promise<void> {
    this.applicationService.showGlobalSpinner();
    this.field = await this.golfEventService.searchField(golfEventId, null);
    this.applicationService.hideGlobalSpinner();
  }

  async toggleHasPaid(fieldPlayer:GolfEventField) {
    await this.golfEventService.updateFieldHasPaid(fieldPlayer.id, fieldPlayer.hasPaid);
  }

  async searchTeams():Promise<void> {
    if(!this.league || !this.isTeamEvent) return;
    this.teams = await this.golfEventService.searchTeams(this.league.id);
    this.teamRows = this.teams.map(team => ({ team, isConfirmingDelete: false }));
  }

  canEditTeam(team:Team):boolean {
    if (this.isEventOver) return false;
    if (this.isEventAdmin) return true;
    if (!this.loggedInPlayerId) return false;
    return team.players.some(p => p.id === this.loggedInPlayerId);
  }

  editTeam(team:Team):void {
    this.teamToEdit = team;
    this.playersForTeamForm = this.buildPlayersForTeamForm(team);
    this.containerDisplay = 'EDITTEAM';
  }

  async deleteTeam(row:TeamRowViewModel):Promise<void> {
    if (!this.isEventAdmin || !this.loggedInPlayerId) return;
    this.applicationService.showGlobalSpinner();
    await this.golfEventService.deleteTeam(row.team.id, this.loggedInPlayerId);
    this.applicationService.hideGlobalSpinner();
    await this.searchTeams();
  }

  async refreshField() {
    if(!this.league) return;
    await this.getField(this.league.id);
    this.containerDisplay = 'INDIVIDUAL';
  }

  async refreshTeams() {
    this.teamToEdit = null;
    this.playersForTeamForm = [];
    await this.searchTeams();
    this.containerDisplay = 'TEAMS';
  }

  cancelEditTeam():void {
    this.teamToEdit = null;
    this.playersForTeamForm = [];
    this.containerDisplay = 'TEAMS';
  }
}
